import { type ReactNode } from 'react';
import { ActionBoardStoreProvider, usePersistActionBoardStore } from '@/actionBoards/actionBoard.store';
import { AdministrationStoreProvider } from '@/administration/administration.store';
import { AgentBuilderStoreProvider } from '@/agentBuilder/agentBuilder.store';
import { AgentBuilderRunStoreProvider } from '@/agentBuilder/run/agentBuilderRun.store';
import { AiAssistantStoreProvider, usePersistAiAssistantStore } from '@/aiAssistant/aiAssistant.store';
import { AnnotationStoreProvider, usePersistAnnotationStore } from '@/annotation/annotation.store';
import { AssetGroupStoreProvider, usePersistAssetGroupStore } from '@/assetGroupEditor/assetGroup.store';
import {
  sqAggregationBinStore,
  sqAiAssistantStore,
  sqAnnotationStore,
  sqAssetGroupStore,
  sqCapsuleGroupStore,
  sqClusteringStoreV2,
  sqCompositeSearchStore,
  sqConditionWithPropertiesStore,
  sqCursorStore,
  sqDigitalFilterStore,
  sqDisplayToolStore,
  sqExploreFolderModalStore,
  sqExploreWorkbookModalStore,
  sqExportExcelPanelStore,
  sqExportODataPanelStore,
  sqFftStore,
  sqHomeScreenStore,
  sqImportDatafileStore,
  sqIsolationForestStoreV2,
  sqItemPropertiesStore,
  sqLayoutStore,
  sqManualConditionStore,
  sqManualSignalStore,
  sqOnnxStore,
  sqOperationalizeStatisticsSummaryStore,
  sqPartialLeastSquaresStoreV2,
  sqPdfExportStore,
  sqPeriodicConditionStore,
  sqPredictionPanelStore,
  sqPrincipalComponentAnalysisStoreV2,
  sqProfileSearchStore,
  sqReferencePanelStore,
  sqReportContentStore,
  sqScalingTemplateStore,
  sqScatterConditionStore,
  sqScorecardStore,
  sqSearchStore,
  sqSelfOrganizingMapsStoreV2,
  sqSignalForecastStore,
  sqSignalFromConditionStore,
  sqSignalSmoothingStore,
  sqStateSynchronizer,
  sqStatisticsSummaryStore,
  sqStoredTableStore,
  sqThresholdMetricStore,
  sqToolTemplateStore,
  sqValueSearchStore,
  sqViewStatisticSummariesStore,
  sqWorkstepsStore,
} from '@/core/core.stores';
import { DashboardStoreProvider, usePersistDashboardStore } from '@/dashboard/dashboard.store';
import { ExploreFolderModalStoreProvider, usePersistExploreFolderModalStore } from '@/explorer/exploreFolderModal.store';
import { ExploreWorkbookModalStoreProvider, usePersistExploreWorkbookModalStore } from '@/explorer/exploreWorkbookModal.store';
import { AddOnToolStoreProvider } from '@/homescreen/addOnTool.store';
import { HomeScreenStoreProvider, usePersistHomeScreenStore } from '@/homescreen/homescreen.store';
import { ImpactReportStoreProvider, usePersistImpactReportStore } from '@/impactReports/impactReports.store';
import { LayoutStoreProvider, usePersistLayoutStore } from '@/main/layout.store';
import { WorkbenchLayoutStoreProvider, usePersistWorkbenchLayoutStore } from '@/main/workbenchLayout.store';
import { PdfExportStoreProvider, usePersistPdfExportStore } from '@/reportEditor/pdfExport.store';
import { ReportContentStoreProvider } from '@/reportEditor/reportContent.store';
import { SearchStoreProvider, usePersistSearchStore } from '@/search/search.store';
import { StoredTableStoreProvider, usePersistStoredTableStore } from '@/storedTableEditor/storedTable.store';
import { PersistenceProvider } from '@/stores/PersistenceProvider';
import { usePersistSummaryReportStore } from '@/summaryReport/summaryReport.store';
import { SystemWarningStoreProvider } from '@/systemWarning/systemWarning.store';
import { ScorecardStoreProvider, usePersistScorecardStore } from '@/tableBuilder/scorecard.store';
import { ScalingTemplateStoreProvider, usePersistScalingTemplateStore } from '@/tableDefinitionEditor/scalingTemplate.store';
import { ClusteringV2StoreProvider, usePersistClusteringV2Store } from '@/tools/clusteringV2/clustering.store';
import { CompositeSearchStoreProvider, usePersistCompositeSearchStore } from '@/tools/compositeSearch/compositeSearch.store';
import { ConditionWithPropertiesStoreProvider, usePersistConditionWithPropertiesStore } from '@/tools/conditionWithProperties/conditionWithProperties.store';
import { DigitalFilterStoreProvider, usePersistDigitalFilterStore } from '@/tools/digitalFilter/digitalFilter.store';
import { DisplayToolStoreProvider, usePersistDisplayToolStore } from '@/tools/display/displayTool.store';
import { ExportExcelPanelStoreProvider, usePersistExportExcelPanelStore } from '@/tools/exportExcel/exportExcelPanel.store';
import { ExportODataPanelStoreProvider, usePersistExportODataPanelStore } from '@/tools/exportOData/exportODataPanel.store';
import { FftStoreProvider, usePersistFftStore } from '@/tools/frequencyAnalysis/fft.store';
import { AggregationBinStoreProvider, usePersistAggregationBinStore } from '@/tools/histogram/aggregationBin.store';
import { ImportDatafileStoreProvider, usePersistImportDatafileStore } from '@/tools/importDatafile/importDatafile.store';
import { IsolationForestV2StoreProvider, usePersistIsolationForestV2Store } from '@/tools/isolationForestV2/isolationForest.store';
import { ItemPropertiesStoreProvider, usePersistItemPropertiesStore } from '@/tools/itemProperties/propertiesPanel.store';
import { CapsuleGroupStoreProvider, usePersistCapsuleGroupStore } from '@/tools/manualCondition/capsuleGroup.store';
import { ManualConditionStoreProvider, usePersistManualConditionStore } from '@/tools/manualCondition/manualCondition.store';
import { ManualSignalStoreProvider, usePersistManualSignalStore } from '@/tools/manualSignal/manualSignal.store';
import { ToolTemplateStoreProvider, usePersistToolTemplateStore } from '@/tools/models/toolTemplate.store';
import { OnnxStoreProvider, usePersistOnnxToolStore } from '@/tools/onnx/onnx.store';
import { OperationalizeStatsSummaryStoreProvider, usePersistOperationalizeStatsSummaryStore } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.store';
import { PartialLeastSquaresV2StoreProvider, usePersistPartialLeastSquaresV2Store } from '@/tools/partialLeastSquaresV2/partialLeastSquares.store';
import { PeriodicConditionStoreProvider, usePersistPeriodicConditionStore } from '@/tools/periodicCondition/periodicCondition.store';
import { PredictionPanelStoreProvider, usePersistPredictionPanelStore } from '@/tools/prediction/predictionPanel.store';
import { PrincipalComponentAnalysisV2StoreProvider, usePersistPrincipalComponentAnalysisV2Store } from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.store';
import { ProfileSearchStoreProvider, usePersistProfileSearchStore } from '@/tools/profileSearch/profileSearch.store';
import { ReferencePanelStoreProvider, usePersistReferencePanelStore } from '@/tools/referencePanel/referencePanel.store';
import { ScatterConditionStoreProvider, usePersistScatterConditionStore } from '@/tools/scatterPlotSelection/scatterCondition.store';
import { SelfOrganizingMapsV2StoreProvider, usePersistSelfOrganizingMapsV2Store } from '@/tools/selfOrganizingMapsV2/selfOrganizingMaps.store';
import { SignalForecastStoreProvider, usePersistSignalForecastStore } from '@/tools/signalForecast/signalForecast.store';
import { SignalFromConditionStoreProvider, usePersistSignalFromConditionStore } from '@/tools/signalFromCondition/signalFromCondition.store';
import { SignalSmoothingStoreProvider, usePersistSignalSmoothingStore } from '@/tools/signalSmoothing/signalSmoothing.store';
import { StatisticsSummaryStoreProvider, usePersistStatisticsSummaryStore } from '@/tools/statisticsSummary/statisticsSummary.store';
import { ThresholdMetricStoreProvider, usePersistThresholdMetricStore } from '@/tools/thresholdMetric/thresholdMetric.store';
import { ViewStatisticSummariesStoreProvider } from '@/tools/viewStatisticSummaries/viewStatisticSummaries.store';
import { TrendStoreProviders } from '@/trendData/TrendStoreProviders';
import { CursorStoreProvider, usePersistCursorStore } from '@/trendData/cursor.store';
import { usePersistTrendScalingColumnStore } from '@/trendData/trendScalingColumn.store';
import { ValueSearchStoreProvider, usePersistValueSearchStore } from '@/trendData/valueSearch.store';
import { VantageTrendStoreProvider, usePersistVantageTrendStore } from '@/vantage/vantageTrend.store';
import { VantageWorkbookStoreProvider } from '@/vantage/vantageWorkbook.store';
import { WorkstepsStoreProvider } from '@/worksteps/worksteps.store';

export const PersistStores = () => {
  // NOTE: this is where each store that needs to be persisted should be added using its custom hook.
  usePersistHomeScreenStore();
  usePersistAiAssistantStore();
  usePersistImpactReportStore();
  usePersistActionBoardStore();
  usePersistAnnotationStore();
  usePersistLayoutStore();
  usePersistCursorStore();
  usePersistTrendScalingColumnStore();
  usePersistVantageTrendStore();
  usePersistOperationalizeStatsSummaryStore();
  usePersistPdfExportStore();
  usePersistSearchStore();
  usePersistExploreFolderModalStore();
  usePersistExploreWorkbookModalStore();
  usePersistDisplayToolStore();
  usePersistExportExcelPanelStore();
  usePersistExportODataPanelStore();
  usePersistItemPropertiesStore();
  usePersistImportDatafileStore();
  usePersistToolTemplateStore();
  usePersistScalingTemplateStore();
  usePersistScorecardStore();
  usePersistStoredTableStore();
  usePersistWorkbenchLayoutStore();
  usePersistDigitalFilterStore();
  usePersistCompositeSearchStore();
  usePersistFftStore();
  usePersistAggregationBinStore();
  usePersistOnnxToolStore();
  usePersistDashboardStore();
  usePersistClusteringV2Store();
  usePersistIsolationForestV2Store();
  usePersistPartialLeastSquaresV2Store();
  usePersistPrincipalComponentAnalysisV2Store();
  usePersistSelfOrganizingMapsV2Store();
  usePersistStatisticsSummaryStore();
  usePersistThresholdMetricStore();
  usePersistPredictionPanelStore();
  usePersistProfileSearchStore();
  usePersistReferencePanelStore();
  usePersistScatterConditionStore();
  usePersistSignalSmoothingStore();
  usePersistSignalForecastStore();
  usePersistSignalFromConditionStore();
  usePersistCapsuleGroupStore();
  usePersistManualConditionStore();
  usePersistManualSignalStore();
  usePersistPeriodicConditionStore();
  usePersistValueSearchStore();
  usePersistSummaryReportStore();
  usePersistConditionWithPropertiesStore();
  usePersistAssetGroupStore();

  return null;
};

export const StoreProviders = ({ children }: { children: ReactNode }) => {
  return (
    <PersistenceProvider stateSynchronizer={sqStateSynchronizer}>
      <AdministrationStoreProvider>
        <ImpactReportStoreProvider>
          <VantageWorkbookStoreProvider>
            <DigitalFilterStoreProvider store={sqDigitalFilterStore}>
              <FftStoreProvider store={sqFftStore}>
                <CapsuleGroupStoreProvider store={sqCapsuleGroupStore}>
                  <AggregationBinStoreProvider store={sqAggregationBinStore}>
                    <AiAssistantStoreProvider store={sqAiAssistantStore}>
                      <WorkbenchLayoutStoreProvider>
                        <ScalingTemplateStoreProvider store={sqScalingTemplateStore}>
                          <ToolTemplateStoreProvider store={sqToolTemplateStore}>
                            <SearchStoreProvider store={sqSearchStore}>
                              <HomeScreenStoreProvider store={sqHomeScreenStore}>
                                <ExploreFolderModalStoreProvider store={sqExploreFolderModalStore}>
                                  <ExploreWorkbookModalStoreProvider store={sqExploreWorkbookModalStore}>
                                    <ActionBoardStoreProvider>
                                      <SystemWarningStoreProvider>
                                        <AnnotationStoreProvider store={sqAnnotationStore}>
                                          <LayoutStoreProvider store={sqLayoutStore}>
                                            <CursorStoreProvider store={sqCursorStore}>
                                              <TrendStoreProviders>
                                                <AddOnToolStoreProvider>
                                                  <AgentBuilderRunStoreProvider>
                                                    <AgentBuilderStoreProvider>
                                                      <VantageTrendStoreProvider>
                                                        <PdfExportStoreProvider store={sqPdfExportStore}>
                                                          <WorkstepsStoreProvider store={sqWorkstepsStore}>
                                                            <StoredTableStoreProvider store={sqStoredTableStore}>
                                                              <DisplayToolStoreProvider store={sqDisplayToolStore}>
                                                                <ExportExcelPanelStoreProvider store={sqExportExcelPanelStore}>
                                                                  <ExportODataPanelStoreProvider store={sqExportODataPanelStore}>
                                                                    <ItemPropertiesStoreProvider store={sqItemPropertiesStore}>
                                                                      <ImportDatafileStoreProvider store={sqImportDatafileStore}>
                                                                        <ConditionWithPropertiesStoreProvider store={sqConditionWithPropertiesStore}>
                                                                          <CompositeSearchStoreProvider store={sqCompositeSearchStore}>
                                                                            <ThresholdMetricStoreProvider store={sqThresholdMetricStore}>
                                                                              <PredictionPanelStoreProvider store={sqPredictionPanelStore}>
                                                                                <ReferencePanelStoreProvider store={sqReferencePanelStore}>
                                                                                  <ClusteringV2StoreProvider store={sqClusteringStoreV2}>
                                                                                    <IsolationForestV2StoreProvider store={sqIsolationForestStoreV2}>
                                                                                      <PartialLeastSquaresV2StoreProvider store={sqPartialLeastSquaresStoreV2}>
                                                                                        <PrincipalComponentAnalysisV2StoreProvider store={sqPrincipalComponentAnalysisStoreV2}>
                                                                                          <SelfOrganizingMapsV2StoreProvider store={sqSelfOrganizingMapsStoreV2}>
                                                                                            <OnnxStoreProvider store={sqOnnxStore}>
                                                                                              <StatisticsSummaryStoreProvider store={sqStatisticsSummaryStore}>
                                                                                                <OperationalizeStatsSummaryStoreProvider store={sqOperationalizeStatisticsSummaryStore}>
                                                                                                  <ScorecardStoreProvider store={sqScorecardStore}>
                                                                                                    <ViewStatisticSummariesStoreProvider store={sqViewStatisticSummariesStore}>
                                                                                                      <SignalForecastStoreProvider store={sqSignalForecastStore}>
                                                                                                        <DashboardStoreProvider>
                                                                                                          <ProfileSearchStoreProvider store={sqProfileSearchStore}>
                                                                                                            <ScatterConditionStoreProvider store={sqScatterConditionStore}>
                                                                                                              <SignalFromConditionStoreProvider store={sqSignalFromConditionStore}>
                                                                                                                <SignalSmoothingStoreProvider store={sqSignalSmoothingStore}>
                                                                                                                  <ManualConditionStoreProvider store={sqManualConditionStore}>
                                                                                                                    <PeriodicConditionStoreProvider store={sqPeriodicConditionStore}>
                                                                                                                      <ManualSignalStoreProvider store={sqManualSignalStore}>
                                                                                                                        <ValueSearchStoreProvider store={sqValueSearchStore}>
                                                                                                                          <AssetGroupStoreProvider store={sqAssetGroupStore}>
                                                                                                                            <ReportContentStoreProvider store={sqReportContentStore}>
                                                                                                                              <PersistStores />
                                                                                                                              {children}
                                                                                                                            </ReportContentStoreProvider>
                                                                                                                          </AssetGroupStoreProvider>
                                                                                                                        </ValueSearchStoreProvider>
                                                                                                                      </ManualSignalStoreProvider>
                                                                                                                    </PeriodicConditionStoreProvider>
                                                                                                                  </ManualConditionStoreProvider>
                                                                                                                </SignalSmoothingStoreProvider>
                                                                                                              </SignalFromConditionStoreProvider>
                                                                                                            </ScatterConditionStoreProvider>
                                                                                                          </ProfileSearchStoreProvider>
                                                                                                        </DashboardStoreProvider>
                                                                                                      </SignalForecastStoreProvider>
                                                                                                    </ViewStatisticSummariesStoreProvider>
                                                                                                  </ScorecardStoreProvider>
                                                                                                </OperationalizeStatsSummaryStoreProvider>
                                                                                              </StatisticsSummaryStoreProvider>
                                                                                            </OnnxStoreProvider>
                                                                                          </SelfOrganizingMapsV2StoreProvider>
                                                                                        </PrincipalComponentAnalysisV2StoreProvider>
                                                                                      </PartialLeastSquaresV2StoreProvider>
                                                                                    </IsolationForestV2StoreProvider>
                                                                                  </ClusteringV2StoreProvider>
                                                                                </ReferencePanelStoreProvider>
                                                                              </PredictionPanelStoreProvider>
                                                                            </ThresholdMetricStoreProvider>
                                                                          </CompositeSearchStoreProvider>
                                                                        </ConditionWithPropertiesStoreProvider>
                                                                      </ImportDatafileStoreProvider>
                                                                    </ItemPropertiesStoreProvider>
                                                                  </ExportODataPanelStoreProvider>
                                                                </ExportExcelPanelStoreProvider>
                                                              </DisplayToolStoreProvider>
                                                            </StoredTableStoreProvider>
                                                          </WorkstepsStoreProvider>
                                                        </PdfExportStoreProvider>
                                                      </VantageTrendStoreProvider>
                                                    </AgentBuilderStoreProvider>
                                                  </AgentBuilderRunStoreProvider>
                                                </AddOnToolStoreProvider>
                                              </TrendStoreProviders>
                                            </CursorStoreProvider>
                                          </LayoutStoreProvider>
                                        </AnnotationStoreProvider>
                                      </SystemWarningStoreProvider>
                                    </ActionBoardStoreProvider>
                                  </ExploreWorkbookModalStoreProvider>
                                </ExploreFolderModalStoreProvider>
                              </HomeScreenStoreProvider>
                            </SearchStoreProvider>
                          </ToolTemplateStoreProvider>
                        </ScalingTemplateStoreProvider>
                      </WorkbenchLayoutStoreProvider>
                    </AiAssistantStoreProvider>
                  </AggregationBinStoreProvider>
                </CapsuleGroupStoreProvider>
              </FftStoreProvider>
            </DigitalFilterStoreProvider>
          </VantageWorkbookStoreProvider>
        </ImpactReportStoreProvider>
      </AdministrationStoreProvider>
    </PersistenceProvider>
  );
};
